// Full-screen SVG discovery map: a thin blit over the pure render model
// (discovery-map-model) + nav reducer (discovery-map-nav). Mounts over the stage;
// receives D-pad NavKeys from PianoControl (and arrow keys directly) while open;
// loads a song on select. Positions come from the dot-laid-out song lattice.
import type { NavKey } from "../input/command-layer";
import type { CatalogEntry } from "../songs/catalog";
import { buildMastery, curriculumState } from "../songs/curriculum-state";
import type { MapLayout } from "../songs/map-layout";
import {
  buildMapRenderModel,
  type MapPanel,
  type MapRenderModel,
  type MapSelection,
} from "./discovery-map-model";
import { type MapNavState, mapNavReduce } from "./discovery-map-nav";
import { el } from "./dom";
import { frontierFocus } from "./frontier-focus";
import { buildRailPanel, drawLattice, MARGIN, NODE_H, NODE_W, svg } from "./lattice-svg";

interface DiscoveryMapHost {
  catalog(): readonly CatalogEntry[];
  layout(): MapLayout;
  masteredIds(): ReadonlySet<string>;
  loadSong(id: string): void;
  /** Show the path (entry/frontier → this song) — works for locked songs. */
  showPath(id: string): void;
  onClose(): void;
}

const KEYMAP: Record<string, NavKey> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  Enter: "select",
};

interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}
/** Clamp a zoom width to [10× in, 1.5× out] of the fit width. */
function clampZoom(w: number, fitW: number): number {
  return Math.max(fitW * 0.1, Math.min(fitW * 1.5, w));
}

export class DiscoveryMap {
  private root: HTMLElement | null = null;
  private nav: MapNavState | null = null;
  /** A clicked edge overrides the node panel (mouse-only); cleared on any node nav. */
  private edgeSel: Extract<MapSelection, { kind: "edge" }> | null = null;
  private opened = false;
  private onKey?: (ev: KeyboardEvent) => void;
  /** Pan/zoom viewBox (F7). Null until the first build computes the fit; persists
   *  across re-renders so navigating doesn't snap the view back, reset on open(). */
  private view: ViewBox | null = null;
  private fitView: ViewBox = { x: 0, y: 0, w: 0, h: 0 };
  private svgEl: SVGSVGElement | null = null;
  /** Path-mode override: when set, render this linear layout instead of the full map. */
  private overrideLayout: MapLayout | null = null;
  /** Frontier-focus (F12): show only mastered ∪ frontier ∪ N hops. Default on (the
   *  learner view); the header toggle switches to the full map. */
  private focus = true;
  private static readonly FOCUS_HOPS = 2;

  constructor(private readonly host: DiscoveryMapHost) {}

  get isOpen(): boolean {
    return this.opened;
  }

  open(): void {
    if (this.opened && this.overrideLayout === null) return; // already showing the map
    this.overrideLayout = null;
    this.focus = true; // every fresh open lands on the learner's focused horizon
    this.mount();
  }

  /** Open in "path mode": render `layout` (a linear path) instead of the full map. The
   *  SAME component — focus, rail, nav, pan/zoom all work; only the data differs. */
  openPath(layout: MapLayout): void {
    this.overrideLayout = layout;
    this.mount();
  }

  /** The active data source: a path override if set; else the full map, narrowed to the
   *  frontier horizon when focus is on. */
  private layout(): MapLayout {
    if (this.overrideLayout) return this.overrideLayout;
    const full = this.host.layout();
    if (!this.focus) return full;
    const { satisfied } = buildMastery(this.host.catalog(), this.host.masteredIds());
    return frontierFocus(full, satisfied, DiscoveryMap.FOCUS_HOPS);
  }

  private mount(): void {
    const layout = this.layout();
    const first = layout.nodes.find((n) => n.isRoot)?.id ?? layout.nodes[0]?.id;
    if (!first) return;
    // Graph mode: arrows move between nodes; the panel always reflects the focused node.
    this.nav = { mode: "graph", nodeId: first, songIndex: 0 };
    this.edgeSel = null;
    this.view = null; // re-fit the viewBox each time
    if (!this.opened) {
      this.opened = true;
      this.onKey = (ev) => {
        const k = KEYMAP[ev.key];
        if (k) {
          ev.preventDefault();
          this.handleNav(k);
        } else if (ev.key === "Escape") {
          this.close();
        }
      };
      document.addEventListener("keydown", this.onKey);
    }
    this.render();
  }

  close(): void {
    this.opened = false;
    if (this.onKey) document.removeEventListener("keydown", this.onKey);
    this.onKey = undefined;
    this.svgEl = null;
    this.edgeSel = null;
    this.overrideLayout = null;
    this.root?.remove();
    this.root = null;
    this.host.onClose();
  }

  handleNav(key: NavKey): void {
    if (!this.opened || !this.nav) return;
    this.edgeSel = null; // D-pad navigates nodes — leave any clicked edge
    const r = mapNavReduce(this.nav, key, this.layout());
    this.nav = r.state;
    if (r.load !== undefined) {
      this.loadEntry(r.load); // any item loads — the lock is advisory, not a wall
      return;
    }
    this.render();
  }

  // Mouse handlers mirror handleNav: they set the same nav state the D-pad drives.
  private focusNode(id: string, hasSongs: boolean): void {
    if (!this.opened) return;
    this.edgeSel = null;
    this.nav = { mode: hasSongs ? "rail" : "graph", nodeId: id, songIndex: 0 };
    this.render();
  }

  private focusEdge(sel: Extract<MapSelection, { kind: "edge" }>): void {
    if (!this.opened) return;
    this.edgeSel = sel;
    this.render();
  }

  private loadEntry(id: string): void {
    if (!this.opened) return;
    this.host.loadSong(id);
    this.close();
  }

  /** The panel reflects a clicked edge, else the D-pad's focused node. */
  private selection(): MapSelection {
    if (this.edgeSel) return this.edgeSel;
    const nav = this.nav;
    return { kind: "node", nodeId: nav?.nodeId ?? "", rowIndex: nav?.songIndex ?? 0 };
  }

  private model(): MapRenderModel {
    const cat = this.host.catalog();
    const mastered = this.host.masteredIds();
    return buildMapRenderModel(
      this.layout(),
      cat,
      curriculumState(cat, mastered),
      mastered,
      this.selection(),
    );
  }

  private render(model?: MapRenderModel): void {
    const m = model ?? this.model();
    const root = el("div", { class: `discovery-map${this.overrideLayout ? " path-mode" : ""}` });
    // Backdrop click closes; node/song handlers stopPropagation so only bare
    // backdrop hits reach here.
    root.onclick = (ev) => {
      if (ev.target === root) this.close();
    };

    const header = el("div", { class: "map-header" });
    const prof = Math.round(m.proficiency * 4) / 4;
    header.append(el("strong", { class: "map-prof", textContent: `Proficiency: ${prof}` }));
    if (!this.overrideLayout) header.append(this.focusButton()); // not in path mode
    header.append(this.closeButton());
    root.append(header);

    const body = el("div", { class: "map-body" });
    body.append(this.buildSvg(m));
    if (m.panel) body.append(this.buildPanel(m.panel));
    root.append(body);

    this.root?.remove();
    this.root = root;
    document.body.append(root);
  }

  private closeButton(): HTMLButtonElement {
    const btn = el<HTMLButtonElement>("button", {
      class: "map-close",
      textContent: "✕ Close",
    });
    btn.onclick = () => this.close();
    return btn;
  }

  /** Toggle the frontier-focus horizon ↔ the full map; re-mount so the view re-fits. */
  private focusButton(): HTMLButtonElement {
    const btn = el<HTMLButtonElement>("button", {
      class: "map-focus-toggle",
      textContent: this.focus ? "🗺 Full map" : "🔭 Focus",
    });
    btn.onclick = () => {
      this.focus = !this.focus;
      this.mount();
    };
    return btn;
  }

  private buildSvg(m: MapRenderModel): SVGSVGElement {
    const maxX = Math.max(0, ...m.nodes.map((n) => n.x)) + NODE_W / 2 + MARGIN;
    const maxY = Math.max(0, ...m.nodes.map((n) => n.y)) + NODE_H / 2 + MARGIN;
    this.fitView = { x: 0, y: 0, w: maxX, h: maxY };
    if (this.view === null) this.view = { ...this.fitView };
    const v = this.view;
    const root = svg("svg", {
      class: "map-svg",
      viewBox: `${v.x} ${v.y} ${v.w} ${v.h}`,
      preserveAspectRatio: "xMidYMid meet",
    });
    this.svgEl = root;
    this.attachPanZoom(root);
    drawLattice(root, m, {
      onNode: (id, hasSongs) => this.focusNode(id, hasSongs),
      onEdge: (sel) => this.focusEdge(sel),
    });
    return root;
  }

  /** Mouse pan (drag the background) + wheel zoom (desktop) + two-finger pinch-zoom and
   *  drag-pan (touch). Double-click / -tap resets to fit. D-pad/keyboard still select. */
  private attachPanZoom(el: SVGSVGElement): void {
    let panning = false;
    let pinching = false;
    let lastX = 0;
    let lastY = 0;
    let pinchDist = 0;
    const dist = (a: Touch, b: Touch): number =>
      Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    el.addEventListener(
      "wheel",
      (ev) => {
        ev.preventDefault();
        const p = this.clientToView(ev.clientX, ev.clientY);
        if (p) this.zoomAt(p, ev.deltaY < 0 ? 0.85 : 1 / 0.85);
      },
      { passive: false },
    );

    el.addEventListener("pointerdown", (ev) => {
      const t = ev.target as Element | null;
      // Only the background pans; node groups keep their click-to-select.
      if (pinching || !t || (t !== el && !t.classList.contains("map-edge"))) return;
      panning = true;
      lastX = ev.clientX;
      lastY = ev.clientY;
      el.setPointerCapture(ev.pointerId);
      el.classList.add("panning");
    });
    el.addEventListener("pointermove", (ev) => {
      const v = this.view;
      const s = this.viewScale();
      if (pinching || !panning || !v || !s) return;
      this.view = { ...v, x: v.x - (ev.clientX - lastX) / s, y: v.y - (ev.clientY - lastY) / s };
      lastX = ev.clientX;
      lastY = ev.clientY;
      this.applyView();
    });
    const end = (ev: PointerEvent): void => {
      if (!panning) return;
      panning = false;
      el.releasePointerCapture(ev.pointerId);
      el.classList.remove("panning");
    };
    el.addEventListener("pointerup", end);
    el.addEventListener("pointercancel", end);

    // Touch pinch-zoom: two fingers, zoom toward their midpoint by the distance ratio.
    el.addEventListener(
      "touchstart",
      (ev) => {
        const [a, b] = [ev.touches[0], ev.touches[1]];
        if (a && b) {
          pinching = true;
          panning = false;
          pinchDist = dist(a, b);
          ev.preventDefault();
        }
      },
      { passive: false },
    );
    el.addEventListener(
      "touchmove",
      (ev) => {
        const [a, b] = [ev.touches[0], ev.touches[1]];
        if (!pinching || !a || !b) return;
        ev.preventDefault();
        const d = dist(a, b);
        const mid = this.clientToView((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2);
        if (mid && pinchDist > 0 && d > 0) this.zoomAt(mid, pinchDist / d); // apart → zoom in
        pinchDist = d;
      },
      { passive: false },
    );
    const touchEnd = (ev: TouchEvent): void => {
      if (ev.touches.length < 2) pinching = false;
    };
    el.addEventListener("touchend", touchEnd);
    el.addEventListener("touchcancel", touchEnd);

    el.addEventListener("dblclick", () => {
      this.view = { ...this.fitView };
      this.applyView();
    });
  }

  /** Zoom so `p` (viewBox coords) stays put, scaling the view width by `factor`. */
  private zoomAt(p: { x: number; y: number }, factor: number): void {
    const v = this.view;
    if (!v) return;
    const nw = clampZoom(v.w * factor, this.fitView.w);
    const nh = v.h * (nw / v.w);
    this.view = {
      x: p.x - (p.x - v.x) * (nw / v.w),
      y: p.y - (p.y - v.y) * (nh / v.h),
      w: nw,
      h: nh,
    };
    this.applyView();
  }

  private applyView(): void {
    const v = this.view;
    if (this.svgEl && v) this.svgEl.setAttribute("viewBox", `${v.x} ${v.y} ${v.w} ${v.h}`);
  }

  /** Uniform px→viewBox scale under `xMidYMid meet` (letterboxed uniform fit). */
  private viewScale(): number | null {
    const v = this.view;
    if (!v || !this.svgEl) return null;
    const r = this.svgEl.getBoundingClientRect();
    return Math.min(r.width / v.w, r.height / v.h);
  }

  /** Map a client point to viewBox coords, accounting for `meet` letterboxing. */
  private clientToView(px: number, py: number): { x: number; y: number } | null {
    const v = this.view;
    const s = this.viewScale();
    if (!v || !s || !this.svgEl) return null;
    const r = this.svgEl.getBoundingClientRect();
    const offX = (r.width - v.w * s) / 2;
    const offY = (r.height - v.h * s) / 2;
    return { x: v.x + (px - r.left - offX) / s, y: v.y + (py - r.top - offY) / s };
  }

  // The persistent context panel: a node's songs, or the drills that unlock an edge's
  // skill/chord. Every row loads on click (the lock is advisory, not a wall).
  private buildPanel(panel: MapPanel): HTMLElement {
    return buildRailPanel(panel, {
      onLoad: (id) => this.loadEntry(id),
      onShowPath: (id) => this.host.showPath(id),
    });
  }
}
